// File: src/pages/EmbeddedAuthRedirect.jsx
// Handles authentication when users click "Open Full Dashboard" from embedded view
// Works for both Shopify and BigCommerce embedded contexts

import React, { useEffect, useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

export default function EmbeddedAuthRedirect() {
  const [searchParams] = useSearchParams();
  const authContext = useContext(AuthContext);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    // Verify we're NOT in an iframe
    if (window.self !== window.top) {
      setError('Still inside the embedded view. Please copy the link and paste it in a new browser window.');
      setStatus('error');
      return;
    }

    const token = searchParams.get('token');
    const shop = searchParams.get('shop'); // Shopify
    const storeHash = searchParams.get('store_hash'); // BigCommerce
    const platform = searchParams.get('platform');
    const redirect = searchParams.get('redirect') || '/dashboard';

    if (!token) {
      setError('No authentication token provided. Please try again.');
      setStatus('error');
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
      return;
    }

    try {
      // Store in session/local storage
      sessionStorage.setItem('jwtToken', token);
      localStorage.setItem('token', token);
      
      // Clear platform-specific embedded flags
      sessionStorage.removeItem('isShopifyEmbedded');
      sessionStorage.removeItem('shopifyEmbeddedToken');
      
      // Store platform context if available
      if (shop) {
        sessionStorage.setItem('shopifyShop', shop);
      }
      if (storeHash) {
        sessionStorage.setItem('bigcommerceStoreHash', storeHash);
      }
      if (platform) {
        sessionStorage.setItem('platform', platform);
      }

      // Update AuthContext if available
      if (authContext && typeof authContext.setToken === 'function') {
        authContext.setToken(token);
      }

      setStatus('success');
      
      // Navigate to dashboard
      setTimeout(() => {
        window.location.href = redirect;
      }, 500);

    } catch (err) {
      setError('Failed to authenticate. Please try again.');
      setStatus('error');
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    }
  }, [searchParams, authContext]);

  const getManualUrl = () => {
    const token = searchParams.get('token');
    const redirect = searchParams.get('redirect') || '/dashboard';
    if (token) {
      return `${window.location.origin}/embedded-login?token=${token}&redirect=${encodeURIComponent(redirect)}`;
    }
    return '';
  };

  if (status === 'error') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
      }}>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #fecaca',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '420px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          
          <h2 style={{ 
            color: '#1f2937', 
            marginBottom: '8px', 
            marginTop: 0,
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Authentication Issue
          </h2>
          <p style={{ 
            color: '#6b7280', 
            marginBottom: '20px',
            fontSize: '15px',
            lineHeight: '1.5'
          }}>
            {error}
          </p>
          
          {window.self !== window.top && (
            <div style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <p style={{ 
                fontSize: '13px', 
                color: '#374151', 
                marginBottom: '8px',
                marginTop: 0
              }}>
                Copy this link and paste it in a new browser window:
              </p>
              <input
                type="text"
                value={getManualUrl()}
                readOnly
                onClick={(e) => {
                  e.target.select();
                  navigator.clipboard?.writeText(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          )}
          
          <p style={{ 
            color: '#9ca3af', 
            fontSize: '13px',
            margin: 0
          }}>
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '400px',
        boxShadow: '0 4px 24px rgba(106, 13, 173, 0.1)',
        border: '1px solid rgba(106, 13, 173, 0.1)',
      }}>
        {/* Animated gradient spinner */}
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
          padding: '3px',
          margin: '0 auto 24px',
          animation: 'pulse 2s ease-in-out infinite',
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #6A0DAD',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        </div>
        
        <style>
          {`
            @keyframes spin { 
              0% { transform: rotate(0deg); } 
              100% { transform: rotate(360deg); } 
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
          `}
        </style>
        
        {/* BaoBrain logo text */}
        <div style={{
          background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '8px',
        }}>
          BaoBrain
        </div>
        
        <h2 style={{ 
          color: '#1f2937', 
          marginBottom: '8px', 
          marginTop: '16px',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          Opening Full Dashboard
        </h2>
        <p style={{ 
          color: '#6b7280', 
          margin: 0,
          fontSize: '14px'
        }}>
          Authenticating and redirecting...
        </p>
      </div>
    </div>
  );
}